/* Quién cargó cada dato.
 *
 * PARA QUÉ. Si un viaje sale mal cargado, hoy no hay forma de saber quién lo
 * escribió: el padrón guarda CUÁNDO se tocó una fila y no QUIÉN. Eso hace que
 * un error termine en una discusión —"yo no fui"— en vez de en una corrección.
 * No es para vigilar a nadie: es para que la pregunta tenga respuesta y se
 * pueda ir a hablar con la persona correcta.
 *
 * SE SELLA EN UN SOLO LUGAR. `createRow` y `updateRow` de `supabaseRealtime`
 * son el cuello por el que pasa TODA la escritura del hub, así que el autor se
 * escribe ahí y no en cada formulario. Repartido por las pantallas, la próxima
 * que se agregue va a nacer sin autor y nadie se va a enterar hasta que haga
 * falta el dato.
 *
 * EL NOMBRE SE CONGELA EN LA FILA (`usuario_nombre`) y no se resuelve al leer.
 * Es la misma decisión que ya tomó `uso_bot` en FlotaBot, y por el mismo
 * motivo: la gente cambia de puesto, se va, y a veces se le corrige el nombre.
 * Resolviéndolo al leer, una carga de marzo mostraría el nombre de hoy — o
 * quedaría en blanco cuando esa persona ya no está, que es justo cuando el
 * dato hace falta.
 *
 * Se guarda igual el MAIL (`usuario`), que es lo que no cambia y lo que
 * permite volver a encontrar a la persona.
 *
 * SÓLO SE SELLA DONDE LA COLUMNA EXISTE. PostgREST rechaza la fila entera si
 * le llega una clave que la tabla no tiene, así que sellar a ciegas
 * convertiría un campo de auditoría en el motivo por el que no se puede
 * cargar nada. La lista de abajo se mueve junto con la migración.
 */

// Las tablas que tienen `usuario` y `usuario_nombre`. Las de sólo lectura o
// derivadas (km_diario, rto, adblue_padron…) no están y no van a estar: nadie
// las escribe a mano, y una columna de autor siempre vacía se lee como un
// error de carga.
export const TABLAS_CON_AUTOR = new Set([
  "asignaciones",
  // Nació acá y su clave es `dni`, no `id`. Estuvo afuera de esta lista
  // desde el principio: la tabla tiene `usuario` y `usuario_nombre`, la
  // pantalla dibuja la columna «Cargó», y las 224 filas están sin autor.
  "chofer_documentos",
  // Quién le asignó el camión a quién. Además de la columna, la tabla tiene
  // un trigger que copia el autor al historial de cambios: sin el sello acá,
  // ese historial diría QUÉ cambió y nunca quién.
  "chofer_tractor",
  "cargas_combustible",
  "clientes",
  "depositos",
  "empresas",
  "eventos",
  "furgones",
  // Partes de enfermo y vacaciones: quién la cargó es la mitad del dato.
  "licencias",
  "personas",
  "proveedores",
  "sectores",
  "stock",
  "tractores",
  "ubicaciones",
  "utilitarios",
  "vehiculos",
  "viajes",
]);

let _actual = null;

/* Lo fija `AuthContext` cuando termina de traer el perfil. Se pasa el nombre
 * ya armado y no el objeto entero: acá sólo hacen falta dos strings, y
 * guardar el perfil completo invitaría a que otro módulo lo lea de acá en vez
 * de del contexto. */
export function fijar(mail, nombre) {
  _actual = mail ? { mail: String(mail).toLowerCase(), nombre: nombre || "" } : null;
}

export function actual() {
  return _actual;
}

export function limpiar() {
  _actual = null;
}

/* Le agrega el autor a lo que se va a escribir.
 *
 * SI NO SE SABE QUIÉN ES, NO SE INVENTA Y NO SE FRENA. Se guarda la fila sin
 * autor. Bloquear la carga porque el perfil todavía no cargó sería cambiar un
 * dato que falta —visible, y que se puede rastrear por la fecha— por un
 * trabajo que no se pudo hacer. Y escribir "desconocido" sería peor: se leería
 * como un usuario real.
 */
export function sellar(tabla, data) {
  if (!TABLAS_CON_AUTOR.has(tabla) || !_actual) return data;
  return { ...data, usuario: _actual.mail, usuario_nombre: _actual.nombre };
}

/* Cómo se llama el que cargó esta fila. Devuelve `null` cuando no hay autor,
 * que la pantalla dibuja como un guion — y no como un chip vacío, que se
 * leería como alguien sin nombre.
 *
 * `porMail` es opcional y sólo sirve para la FOTO y para las filas viejas:
 * hay cinco tablas que venían guardando el mail sin el nombre, y para ésas
 * todavía hay que ir a buscarlo. Las nuevas ya lo traen adentro.
 */
export function autorDe(fila, porMail = null) {
  const mail = fila?.usuario || fila?.quien || null;
  if (!mail) return null;
  /* LO CARGADO ANTES DEL SELLO DICE "CantApp", y no es un usuario: es el
   * sistema original de Firestore del que salió el padrón entero y del que lo
   * sigue trayendo el sync de las 06:10.
   *
   * Un guion ahí no decía nada — obligaba a preguntarse si el dato era viejo o
   * si el sello estaba fallando. Nombrar el sistema contesta las dos de una.
   * No se le busca foto ni perfil: no hay persona detrás. */
  if (String(mail).toLowerCase() === "cantapp") {
    return { mail: "cantapp", nombre: "CantApp", fotoUrl: null,
             congelado: true, sistema: true };
  }
  const clave = String(mail).toLowerCase();
  const perfil = porMail ? porMail[clave] : null;
  const congelado = fila?.usuario_nombre || fila?.quien_nombre || "";
  const delPadron = perfil
    ? `${perfil.nombres || ""} ${perfil.apellido || ""}`.trim()
    : "";
  return {
    mail: clave,
    // Gana el congelado: es el que estaba vigente cuando se cargó.
    nombre: congelado || delPadron || clave,
    fotoUrl: perfil?.foto_url || null,
    // Para el hover: si el nombre salió del padrón y no de la fila, es un dato
    // de hoy sobre una carga de antes. No cambia lo que se muestra, pero sí
    // lo que se puede afirmar.
    congelado: Boolean(congelado),
  };
}
